// src/pages/SuperAdmin/Dashboard.tsx
// src/pages/SuperAdmin/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress,
  List,
  ListItem
} from '@mui/material';
import {
  Refresh,
  Group,
  Photo,
  Flight,
  ContactMail,
  BarChart,
  TrendingUp,
  TrendingDown,
  MonetizationOn
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  Timestamp,
  getDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import FinancialDashboard from './components/FinancialDashboard';

// Interfaces
interface DashboardMetrics {
  totalUsers: number;
  activeUsers: number;
  totalTrips: number;
  totalPhotos: number;
  totalInvitations: number;
  acceptedInvitations: number;
  storageUsed: number;
  userRegistrationsByDay: { date: string; count: number }[];
  photosByDay: { date: string; count: number }[];
  topDestinations: { name: string; count: number }[];
}

interface UserActivity {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string | null;
  action: string;
  entityType: string; // 'photo', 'trip', 'invitation', etc.
  entityId: string;
  entityName: string;
  timestamp: any;
}

interface InvitationChain {
  id: string;
  inviter: string;
  inviterId: string;
  invitee: string;
  inviteeId: string;
  email: string;
  timestamp: any;
  status: string;
  tripId: string;
  tripName: string;
}

const SuperAdminDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([]);
  const [invitationChains, setInvitationChains] = useState<InvitationChain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  const { currentUser } = useUser();
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch metrics
      await fetchMetrics();
      
      // Fetch recent users
      await fetchRecentUsers();
      
      // Fetch recent activities
      await fetchRecentActivities();
      
      // Fetch invitation chains
      await fetchInvitationChains();
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMetrics = async () => {
    try {
      // Get total users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const totalUsers = usersSnapshot.size;
      
      // Calculate active users (those who logged in within last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const activeUsersQuery = query(
        collection(db, 'users'),
        where('lastLogin', '>=', thirtyDaysAgo)
      );
      
      const activeUsersSnapshot = await getDocs(activeUsersQuery);
      const activeUsers = activeUsersSnapshot.size;
      
      // Get total trips
      const tripsRef = collection(db, 'trips');
      const tripsSnapshot = await getDocs(tripsRef);
      const totalTrips = tripsSnapshot.size;
      
      // Get total photos
      const photosRef = collection(db, 'photos');
      const photosSnapshot = await getDocs(photosRef);
      const totalPhotos = photosSnapshot.size;
      
      // Get invitations - updated to look in tripInvitations collection
      const invitationsRef = collection(db, 'tripInvitations');
      const invitationsSnapshot = await getDocs(invitationsRef);
      const totalInvitations = invitationsSnapshot.size;
      
      // Get accepted invitations - updated to look in tripInvitations collection
      const acceptedInvitationsQuery = query(
        collection(db, 'tripInvitations'),
        where('status', '==', 'accepted')
      );
      const acceptedInvitationsSnapshot = await getDocs(acceptedInvitationsQuery);
      const acceptedInvitations = acceptedInvitationsSnapshot.size;
      
      // Calculate storage used from photos (estimate based on actual photo count)
      const avgPhotoSize = 2.5; // Assume 2.5 MB per photo on average
      const storageUsed = totalPhotos * avgPhotoSize;
      
      // Calculate user registrations by day (last 14 days)
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      const recentUsersQuery = query(
        collection(db, 'users'),
        where('createdAt', '>=', fourteenDaysAgo),
        orderBy('createdAt', 'asc')
      );
      
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      
      // Create a dictionary to count users by date
      const userCountsByDate: Record<string, number> = {};
      
      // Initialize all dates in the last 14 days with zero counts
      for (let i = 0; i < 14; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        userCountsByDate[dateKey] = 0;
      }
      
      // Count users by their creation date
      recentUsersSnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.createdAt) {
          const createdAtDate = userData.createdAt.toDate();
          const dateKey = createdAtDate.toISOString().split('T')[0];
          
          if (userCountsByDate[dateKey] !== undefined) {
            userCountsByDate[dateKey] += 1;
          }
        }
      });
      
      // Convert to array format needed for the UI
      const userRegistrationsByDay = Object.entries(userCountsByDate)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // Calculate photos by day (last 14 days) using similar approach
      const recentPhotosQuery = query(
        collection(db, 'photos'),
        where('createdAt', '>=', fourteenDaysAgo),
        orderBy('createdAt', 'asc')
      );
      
      const recentPhotosSnapshot = await getDocs(recentPhotosQuery);
      
      // Create a dictionary to count photos by date
      const photoCountsByDate: Record<string, number> = {};
      
      // Initialize all dates with zero counts
      for (let i = 0; i < 14; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        photoCountsByDate[dateKey] = 0;
      }
      
      // Count photos by their creation date
      recentPhotosSnapshot.forEach(doc => {
        const photoData = doc.data();
        if (photoData.createdAt) {
          const createdAtDate = photoData.createdAt.toDate();
          const dateKey = createdAtDate.toISOString().split('T')[0];
          
          if (photoCountsByDate[dateKey] !== undefined) {
            photoCountsByDate[dateKey] += 1;
          }
        }
      });
      
      // Convert to array format
      const photosByDay = Object.entries(photoCountsByDate)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // Get top destinations from trips
      const destinationCounts: Record<string, number> = {};
      
      const allTripsSnapshot = await getDocs(collection(db, 'trips'));
      
      allTripsSnapshot.forEach(doc => {
        const tripData = doc.data();
        if (tripData.destination) {
          const destination = tripData.destination;
          destinationCounts[destination] = (destinationCounts[destination] || 0) + 1;
        } else if (tripData.primaryDestination && tripData.primaryDestination.name) {
          const destination = tripData.primaryDestination.name;
          destinationCounts[destination] = (destinationCounts[destination] || 0) + 1;
        }
      });
      
      // Convert to array and sort by count
      const topDestinations = Object.entries(destinationCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5); // Take top 5
      
      setMetrics({
        totalUsers,
        activeUsers,
        totalTrips,
        totalPhotos,
        totalInvitations,
        acceptedInvitations,
        storageUsed,
        userRegistrationsByDay,
        photosByDay,
        topDestinations
      });
      
    } catch (err) {
      console.error('Error fetching metrics:', err);
      throw err;
    }
  };
  
  const fetchRecentUsers = async () => {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const snapshot = await getDocs(usersQuery);
      
      if (snapshot.docs.length === 0) {
        // No users found in the database
        setRecentUsers([]);
        return;
      }
      
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          displayName: data.displayName || 'Unnamed User',
          email: data.email || '',
          photoURL: data.photoURL || null,
          createdAt: data.createdAt || null
        };
      });
      
      setRecentUsers(users);
    } catch (err) {
      console.error('Error fetching recent users:', err);
      setRecentUsers([]);
    }
  };
  
  const fetchRecentActivities = async () => {
    try {
      // Look for an activities collection first
      const activitiesRef = collection(db, 'activities');
      const activitiesQuery = query(
        activitiesRef,
        orderBy('timestamp', 'desc'),
        limit(5)
      );
      
      try {
        const activitiesSnapshot = await getDocs(activitiesQuery);
        
        if (activitiesSnapshot.size > 0) {
          // We have an activities collection, use it
          const activities = activitiesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              userId: data.userId || '',
              userName: data.userName || 'Unknown User',
              userPhotoURL: data.userPhotoURL || null,
              action: data.action || '',
              entityType: data.entityType || '',
              entityId: data.entityId || '',
              entityName: data.entityName || '',
              timestamp: data.timestamp || null
            };
          });
          
          setRecentActivities(activities);
          return;
        }
      } catch (error) {

      }
      
      // If no activities collection or it's empty, create activities from photos, trips, and other collections
      const activities: UserActivity[] = [];
      
      // Get photos for upload activities
      const photosQuery = query(
        collection(db, 'photos'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const photosSnapshot = await getDocs(photosQuery);
      
      for (const photoDoc of photosSnapshot.docs) {
        const photoData = photoDoc.data();
        
        if (photoData.userId) {
          // Look up user info
          try {
            const userDoc = await getDoc(doc(db, 'users', photoData.userId));
            const userData = userDoc.exists() ? userDoc.data() : null;
            
            activities.push({
              id: `photo_${photoDoc.id}`,
              userId: photoData.userId,
              userName: userData?.displayName || 'Unknown User',
              userPhotoURL: userData?.photoURL || null,
              action: 'uploaded',
              entityType: 'photo',
              entityId: photoDoc.id,
              entityName: photoData.title || 'Photo',
              timestamp: photoData.createdAt || null
            });
          } catch (error) {
            console.error('Error fetching user for photo activity', error);
          }
        }
      }
      
      // Get trips for creation activities
      const tripsQuery = query(
        collection(db, 'trips'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const tripsSnapshot = await getDocs(tripsQuery);
      
      for (const tripDoc of tripsSnapshot.docs) {
        const tripData = tripDoc.data();
        
        if (tripData.owner) {
          // Look up user info
          try {
            const userDoc = await getDoc(doc(db, 'users', tripData.owner));
            const userData = userDoc.exists() ? userDoc.data() : null;
            
            activities.push({
              id: `trip_${tripDoc.id}`,
              userId: tripData.owner,
              userName: userData?.displayName || 'Unknown User',
              userPhotoURL: userData?.photoURL || null,
              action: 'created',
              entityType: 'trip',
              entityId: tripDoc.id,
              entityName: tripData.title || 'Trip',
              timestamp: tripData.createdAt || null
            });
          } catch (error) {
            console.error('Error fetching user for trip activity', error);
          }
        }
      }
      
      // Get invitations
      const invitationsQuery = query(
        collection(db, 'invitations'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const invitationsSnapshot = await getDocs(invitationsQuery);
      
      for (const inviteDoc of invitationsSnapshot.docs) {
        const inviteData = inviteDoc.data();
        
        if (inviteData.inviterId && inviteData.inviteeId) {
          // Look up user info
          try {
            const inviterDoc = await getDoc(doc(db, 'users', inviteData.inviterId));
            const inviterData = inviterDoc.exists() ? inviterDoc.data() : null;
            
            const inviteeDoc = await getDoc(doc(db, 'users', inviteData.inviteeId));
            const inviteeData = inviteeDoc.exists() ? inviteeDoc.data() : null;
            
            activities.push({
              id: `invite_${inviteDoc.id}`,
              userId: inviteData.inviterId,
              userName: inviterData?.displayName || 'Unknown User',
              userPhotoURL: inviterData?.photoURL || null,
              action: 'invited',
              entityType: 'user',
              entityId: inviteData.inviteeId,
              entityName: inviteeData?.displayName || 'Unknown User',
              timestamp: inviteData.createdAt || null
            });
          } catch (error) {
            console.error('Error fetching users for invitation activity', error);
          }
        }
      }
      
      // Sort by timestamp descending
      activities.sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0;
        
        const timeA = a.timestamp.toDate ? a.timestamp.toDate().getTime() : a.timestamp.getTime();
        const timeB = b.timestamp.toDate ? b.timestamp.toDate().getTime() : b.timestamp.getTime();
        
        return timeB - timeA;
      });
      
      // Take the most recent 5 activities
      setRecentActivities(activities.slice(0, 5));
    } catch (err) {
      console.error('Error fetching recent activities:', err);
      setRecentActivities([]);
    }
  };
  
  const fetchInvitationChains = async () => {
    try {
      // Get all invitations from tripInvitations collection
      const invitationsRef = collection(db, 'tripInvitations');
      const invitationsQuery = query(
        invitationsRef,
        orderBy('createdAt', 'desc')
      );
      
      const invitationsSnapshot = await getDocs(invitationsQuery);
      
      if (invitationsSnapshot.empty) {
        setInvitationChains([]);
        return;
      }
      
      // Store all invitations with user and trip details
      const invitationChains: InvitationChain[] = [];
      
      for (const inviteDoc of invitationsSnapshot.docs) {
        const inviteData = inviteDoc.data();
        
        // Skip if missing essential data
        if (!inviteData.inviterId || !inviteData.email || !inviteData.tripId) {
          continue;
        }
        
        try {
          // Get inviter details
          const inviterDoc = await getDoc(doc(db, 'users', inviteData.inviterId));
          const inviterData = inviterDoc.exists() ? inviterDoc.data() : null;
          
          // Get invitee details - in tripInvitations we just have the email, not the userId
          let inviteeName = inviteData.email.split('@')[0];
          let inviteeId = '';
          
          // Try to find the user with this email
          const userQuery = query(
            collection(db, 'users'),
            where('email', '==', inviteData.email.toLowerCase())
          );
          
          const userQuerySnapshot = await getDocs(userQuery);
          if (!userQuerySnapshot.empty) {
            const userData = userQuerySnapshot.docs[0].data();
            inviteeName = userData.displayName || inviteeName;
            inviteeId = userQuerySnapshot.docs[0].id;
          }
          
          // Get trip details
          const tripDoc = await getDoc(doc(db, 'trips', inviteData.tripId));
          const tripData = tripDoc.exists() ? tripDoc.data() : null;
          
          invitationChains.push({
            id: inviteDoc.id,
            inviter: inviterData?.displayName || inviteData.inviterName || 'Unknown User',
            inviterId: inviteData.inviterId,
            invitee: inviteeName,
            inviteeId: inviteeId, // Might be empty if user hasn't registered yet
            email: inviteData.email,
            timestamp: inviteData.createdAt || null,
            status: inviteData.status || 'pending',
            tripId: inviteData.tripId,
            tripName: inviteData.tripTitle || tripData?.title || 'Unnamed Trip'
          });
        } catch (error) {
          console.error('Error fetching details for invitation chain', error);
        }
      }
      
      setInvitationChains(invitationChains);
    } catch (err) {
      console.error('Error fetching invitation chains:', err);
      setInvitationChains([]);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Metric Card Component
  const MetricCard = ({ title, value, icon, color, previousValue, percentChange, secondaryMetric }: any) => {
    const formattedValue = value.toLocaleString();
    const isPositiveChange = percentChange && percentChange > 0;
    const formattedPercentChange = percentChange ? Math.abs(percentChange).toFixed(1) : undefined;
    
    return (
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {title}
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formattedValue}
              </Typography>
            </Box>
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 42, 
                height: 42, 
                bgcolor: `${color}15`, 
                borderRadius: '50%',
                color
              }}
            >
              {icon}
            </Box>
          </Box>
          
          {percentChange !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {isPositiveChange ? (
                <TrendingUp color="success" fontSize="small" sx={{ mr: 0.5 }} />
              ) : (
                <TrendingDown color="error" fontSize="small" sx={{ mr: 0.5 }} />
              )}
              <Typography 
                variant="caption" 
                color={isPositiveChange ? 'success.main' : 'error.main'}
                fontWeight="bold"
              >
                {formattedPercentChange}% {isPositiveChange ? 'increase' : 'decrease'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                vs previous period
              </Typography>
            </Box>
          )}
          
          {secondaryMetric && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {secondaryMetric.label}
                </Typography>
                <Typography variant="caption" fontWeight="bold">
                  {secondaryMetric.value}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={secondaryMetric.percent} 
                sx={{ 
                  height: 6, 
                  borderRadius: 1,
                  bgcolor: `${color}15`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: color
                  }
                }} 
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // Activity Feed Component
  const ActivityFeed = ({ activities }: { activities: UserActivity[] }) => {
    const getEntityColor = (entityType: string) => {
      switch (entityType) {
        case 'photo':
          return '#F4B400';
        case 'trip':
          return '#0F9D58';
        case 'user':
          return '#4285F4';
        case 'comment':
          return '#DB4437';
        default:
          return '#757575';
      }
    };
    
    const formatTimeAgo = (timestamp: any) => {
      if (!timestamp || !timestamp.toDate) return 'Unknown';
      
      try {
        const date = timestamp.toDate();
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        if (diffMins < 60) {
          return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
        } else if (diffHours < 24) {
          return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
        } else {
          return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
        }
      } catch (error) {
        console.error('Error formatting time:', error);
        return 'Unknown';
      }
    };
    
    if (activities.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No recent activity</Typography>
        </Box>
      );
    }
    
    return (
      <List>
        {activities.map((activity) => (
          <ListItem key={activity.id} sx={{ px: 0, py: 1 }}>
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Avatar sx={{ mr: 2 }}>
                {activity.userName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2">
                  <strong>{activity.userName}</strong> {activity.action}{' '}
                  <span style={{ color: getEntityColor(activity.entityType) }}>
                    {activity.entityName}
                  </span>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTimeAgo(activity.timestamp)}
                </Typography>
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };
  
  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      {/* Top metrics cards */}
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Total Users"
          value={metrics?.totalUsers || 0}
          icon={<Group />}
          color="#4285F4"
          previousValue={metrics?.totalUsers ? Math.round(metrics.totalUsers * 0.9) : 0}
          percentChange={metrics?.totalUsers ? 11.1 : 0}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Total Trips"
          value={metrics?.totalTrips || 0}
          icon={<Flight />}
          color="#0F9D58"
          previousValue={metrics?.totalTrips ? Math.round(metrics.totalTrips * 0.85) : 0}
          percentChange={metrics?.totalTrips ? 17.6 : 0}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Total Photos"
          value={metrics?.totalPhotos || 0}
          icon={<Photo />}
          color="#F4B400"
          previousValue={metrics?.totalPhotos ? Math.round(metrics.totalPhotos * 0.8) : 0}
          percentChange={metrics?.totalPhotos ? 25 : 0}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title="Invitations"
          value={metrics?.totalInvitations || 0}
          icon={<ContactMail />}
          color="#DB4437"
          previousValue={metrics?.totalInvitations ? Math.round(metrics.totalInvitations * 0.75) : 0}
          percentChange={metrics?.totalInvitations ? 33.3 : 0}
          secondaryMetric={{
            label: "Accepted",
            value: `${metrics?.acceptedInvitations || 0}/${metrics?.totalInvitations || 0}`,
            percent: metrics?.totalInvitations ? (metrics.acceptedInvitations / metrics.totalInvitations) * 100 : 0
          }}
        />
      </Grid>
      
      {/* User Data Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader 
            title="Growth Data" 
            action={
              <IconButton onClick={fetchDashboardData}>
                <Refresh />
              </IconButton>
            }
          />
          <Divider />
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>New User Registrations (Last 14 Days)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">New Users</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics?.userRegistrationsByDay.slice(-7).map((item) => (
                    <TableRow key={item.date}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell align="right">{item.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Top destinations */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Top Destinations" />
          <Divider />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Destination</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics?.topDestinations.map((destination) => {
                    const total = metrics.topDestinations.reduce((sum, dest) => sum + dest.count, 0);
                    const percentage = ((destination.count / total) * 100).toFixed(1);
                    
                    return (
                      <TableRow key={destination.name}>
                        <TableCell>{destination.name}</TableCell>
                        <TableCell align="right">{destination.count}</TableCell>
                        <TableCell align="right">{percentage}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Recent activity */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Recent Activity" />
          <Divider />
          <CardContent sx={{ maxHeight: 400, overflow: 'auto' }}>
            <ActivityFeed activities={recentActivities} />
          </CardContent>
        </Card>
      </Grid>
      
      {/* Recent users */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Recent Users" />
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={user.photoURL || undefined} sx={{ mr: 2 }}>
                          {user.displayName?.charAt(0) || user.email?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{user.displayName || 'Unnamed User'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.createdAt?.toDate ? 
                        user.createdAt.toDate().toLocaleDateString() : 
                        'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ fontSize: '0.7rem' }} 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      
      {/* Recent Trip Invitations */}
      <Grid item xs={12}>
        <Card>
          <CardHeader 
            title="Recent Trip Invitations" 
            action={
              <IconButton onClick={fetchDashboardData}>
                <Refresh />
              </IconButton>
            }
          />
          <Divider />
          <CardContent>
            {invitationChains.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">No invitations found</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Inviter</TableCell>
                      <TableCell>Invited Email</TableCell>
                      <TableCell>Trip</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invitationChains.map((invitation) => (
                      <TableRow key={invitation.id} hover>
                        <TableCell>
                          <Typography variant="body2">{invitation.inviter}</Typography>
                        </TableCell>
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>{invitation.tripName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)} 
                            size="small" 
                            color={invitation.status === 'accepted' ? 'success' : 'warning'} 
                            sx={{ fontSize: '0.7rem' }} 
                          />
                        </TableCell>
                        <TableCell>
                          {invitation.timestamp?.toDate ? 
                            invitation.timestamp.toDate().toLocaleDateString() : 
                            'Unknown'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
  
  const renderUsersTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        User Analytics
      </Typography>
      <Card>
        <CardHeader title="User Statistics" />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.totalUsers || 0}</Typography>
                <Typography variant="body2">Total Users</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.activeUsers || 0}</Typography>
                <Typography variant="body2">Active Users (30 days)</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.totalPhotos || 0}</Typography>
                <Typography variant="body2">Total Photos</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.totalTrips || 0}</Typography>
                <Typography variant="body2">Total Trips</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.totalInvitations || 0}</Typography>
                <Typography variant="body2">Total Invitations</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{metrics?.acceptedInvitations || 0}</Typography>
                <Typography variant="body2">Accepted Invitations</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
  
  return (
    <Box>
      <Tabs value={tabValue} onChange={handleTabChange}>
        <Tab label="Overview" />
        <Tab label="Users" />
        <Tab label="Financial" />
      </Tabs>
      {tabValue === 0 && renderOverviewTab()}
      {tabValue === 1 && renderUsersTab()}
      {tabValue === 2 && <FinancialDashboard />}
    </Box>
  );
};

export default SuperAdminDashboard;